/* eslint-disable jsx-a11y/no-onchange */
import { useState } from "react"
import PropTypes from "prop-types"
import { Table, DatePicker } from "components/ui"
import { Button } from "@planningcenter/tapestry-react"
import { axiosRequest } from "utils/api"
import { isPresent, isBlank, get, stringIncludes } from "utils/helpers"
import { colors } from "components/shared/admin_colors"
import TimePicker from "components/ui/TimePicker"
import moment from "moment-timezone"
import sorter from "utils/sorter"
import PotentialAttendeeAlert from "./PotentialAttendeesAlert"
import AttendanceReportName from "components/admin/Event/AttendanceReportName"

const flattenResponse = response => ({
  id: response.id,
  ...response.attributes
})

const AttendanceReports = ({ attendanceReport, event, eventTimes, eventPaths }) => {
  const [attendanceTakenAt, setAttendanceTakenAt] = useState(
    attendanceReport.data.attributes.attendance_taken_at || moment().format()
  )
  const [errors, setErrors] = useState({})
  const [report, setReport] = useState(flattenResponse(attendanceReport.data))
  const [name, setName] = useState(report.name || "")
  const [eventTimeId, setEventTimeId] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [validation, setValidation] = useState({})
  const [statusMessage, setStatusMessage] = useState(null)

  const [attendanceRecords, setAttendanceRecords] = useState(
    attendanceReport.included
      .filter(r => r.type === "AttendanceRecord")
      .map(r => flattenResponse(r))
  )

  const handleDoneClick = () =>
    window.location.assign(
      `${eventPaths.show}?show_attendance=true&attendance_report_id=${report.id}`
    )

  const handleCancelClick = () => {
    setShowForm(false)
    setName(report.name)
  }

  const handleSaveClick = () => {
    if (isPresent(validateForm())) {
      return
    }

    return axiosRequest("PATCH", `events/${event.id}/attendance_reports/${report.id}`, {
      attributes: {
        name,
        attendance_taken_at: attendanceTakenAt,
        ...(eventTimeId && { event_time_id: eventTimeId })
      }
    }).then(resp => {
      resp.errors ? setErrors(resp.errors) : handleSuccess(resp)
    })
  }

  const handleSuccess = resp => {
    setReport(flattenResponse(resp.data))
    setShowForm(false)
    setErrors({})
  }

  const validateForm = () => {
    let validation = {}

    if (isBlank(name)) {
      validation.name = "Name can not be blank."
    }

    setValidation(validation)
    return validation
  }

  const renderHeaderText = () => {
    return !showForm ? (
      <AttendanceReportName attendanceReport={report} />
    ) : (
      <input
        className={`${validation.name && "error-input"}`}
        type="text"
        name="name"
        placeholder="Name"
        value={name}
        onChange={e => setName(e.target.value)}
        css={{
          marginTop: "-6px !important",
          height: "30px !important",
          maxWidth: 300
        }}
      />
    )
  }

  const renderHeaderDetails = () => {
    return (
      <div className="d-f ai-c mt-1">
        <div className="ws-nw label pr-1 mb-0">Attendance for:</div>
        {showForm && eventTimes.length > 1 ? renderHeaderForm() : renderHeaderDescription()}
      </div>
    )
  }

  const renderHeaderDescription = () => {
    const id = eventTimeId || get(attendanceReport, "data.relationships.event_time.data.id")

    const eventTime = id ? eventTimes.find(et => et.id.toString() === id.toString()) : null

    return eventTime ? eventTime.dateTimeToString : report.description
  }

  const renderHeaderForm = () => {
    return eventTimes.length >= 1 ? renderEventTimeForm() : renderDateTimeForm()
  }

  const renderEventTimeForm = () => {
    return (
      <select
        className="select"
        name="event_time"
        value={eventTimeId ? eventTimeId.toString() : ""}
        onChange={e => setEventTimeId(e.target.value)}
        css={{ height: 30 }}
      >
        <option value="" disabled>
          Select date and time
        </option>

        {eventTimes.map(et => (
          <option key={et.id} value={et.id}>
            {et.dateTimeToString}
          </option>
        ))}
      </select>
    )
  }

  const renderDateTimeForm = () => {
    return (
      <div className="d-f@sm">
        <div className="mb-1 mb-0@sm pr-1@sm f-1">
          <DatePicker
            onChange={setAttendanceTakenAt}
            classNames={`date-picker ${errors.attendance_taken_at ? "error-input" : null}`}
            datetime={attendanceTakenAt}
            style={{ height: 30 }}
          />
        </div>
        <div className="pl-1@sm f-1">
          <TimePicker
            datetime={attendanceTakenAt}
            twentyFourHourTime={App.currentOrganization.twentyFourHourTime}
            mobileBrowser={window.isMobile}
            classNames="time-picker"
            onChange={setAttendanceTakenAt}
            style={{ width: 100, height: 30 }}
          />
        </div>
      </div>
    )
  }

  const renderHeaderActions = () => {
    return !showForm
      ? [
          {
            onClick: () => setShowForm(true),
            title: "Edit"
          }
        ]
      : [
          {
            onClick: handleCancelClick,
            title: "Cancel"
          },
          {
            onClick: handleSaveClick,
            theme: "create",
            title: "Save",
            variant: "fill"
          }
        ]
  }

  const renderAvatar = (row, _col) => (
    <img src={row.avatar} alt={row.name} className="p-r t-2p w-4 h-4 circle" />
  )

  const columns = [
    {
      cell: "avatar",
      render: renderAvatar,
      className: "pl-4p pr-4p"
    },
    {
      label: "First Name",
      cell: "first_name",
      isSortable: true
    },
    {
      label: "Last Name",
      cell: "last_name",
      isSortable: true
    },
    {
      label: "Selection",
      cell: "attendee_type",
      isSortable: true
    }
  ]

  const handleAttendanceChange = (row, sortBy, sortDir, e) => {
    setStatusMessage({
      text: "Saving...",
      icon: "spinner"
    })

    return axiosRequest("PATCH", `events/${event.id}/attendance_records/${row.id}`, {
      attributes: { attended: e.target.checked }
    })
      .then(res => {
        const newRecords = attendanceRecords.map(ar => {
          if (ar.id === res.data.id) {
            return flattenResponse(res.data)
          } else {
            return ar
          }
        })

        setStatusMessage({
          text: "Saved!",
          icon: "general#check",
          type: "success"
        })
        setAttendanceRecords(sorter(sortBy, newRecords, sortDir))
      })
      .catch(_res => {
        setStatusMessage({
          text: "Changes not saved",
          icon: "general#exclamation-triangle",
          type: "error"
        })
      })
      .finally(_r => setTimeout(() => setStatusMessage(null), 3000))
  }

  const handleSearchChange = ({ items, searchValue: value }) => {
    const searchValue = value.trim()
    const searchList = searchValue.split(" ")

    return items.filter(item => {
      const includesFirstName = searchList.some(search => stringIncludes(item.first_name, search))
      const includesLastName = searchList.some(search => stringIncludes(item.last_name, search))

      return (includesFirstName && includesLastName) || stringIncludes(item.name, searchValue)
    })
  }

  const rows = [
    ...attendanceRecords,
    {
      id: "footer",
      // eslint-disable-next-line react/display-name
      render: (_row, _columns) => {
        return (
          <>
            <td colSpan={2} style={{ fontWeight: "bold" }}>
              Total:
            </td>
            <td
              colSpan={columns.length - 1}
              style={{
                fontWeight: "bold",
                paddingTop: "0.5rem",
                paddingBottom: "0.5rem"
              }}
            >
              {attendanceRecords.filter(ar => ar.attended).length} / {attendanceRecords.length}
            </td>
          </>
        )
      }
    }
  ]

  return (
    <div className="mx-a max-width-780">
      <PotentialAttendeeAlert eventId={event.id} attendanceReport={attendanceReport} />
      <Table
        columns={columns}
        rows={rows}
        heading={{
          headerText: renderHeaderText(),
          headerActions: renderHeaderActions(),
          headerSubText: renderHeaderDetails(),
          style: {
            position: "relative",
            zIndex: 2,
            borderBottom: `1px solid ${colors.neutral_060}`,
            "--pane--border-radius": "4px 4px 0 0"
          }
        }}
        headingChildren={
          !showForm && <Button theme="create" onClick={handleDoneClick} title="Done" />
        }
        onSelectChange={handleAttendanceChange}
        onRowClick={(_row, target) => {
          target.currentTarget.firstChild.querySelector("input").click()
        }}
        selectableField="attended"
        status={statusMessage}
        defaultSort="last_name"
        onSearchChange={handleSearchChange}
        searchInputStyle={{
          "--text-input--border-radius": 0,
          "--text-input--box-shadow": "none"
        }}
        overflowWrapperStyle={{
          "--table-wrapper--border-radius": "0 0 4px 4px"
        }}
        style={{ borderTop: "1px solid var(--default-border-color" }}
      />
    </div>
  )
}

AttendanceReports.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.number
  }),
  attendanceReport: PropTypes.shape({
    name: PropTypes.string,
    data: PropTypes.shape({
      attributes: PropTypes.shape({
        attendance_taken_at: PropTypes.string,
        potential_attendees_count: PropTypes.number
      })
    }),
    included: PropTypes.array
  }),
  eventTimes: PropTypes.array,
  eventPaths: PropTypes.shape({
    show: PropTypes.string
  })
}

export default AttendanceReports
